<template>
  <div class="app">

    <AppHeader fixed>
      <SidebarToggler v-if="!mySpaceMode" class="d-lg-none sidebar-trigger" display="md" mobile :default-open="!mySpaceMode" />
      <SidebarToggler v-if="!mySpaceMode" class="d-md-down-none sidebar-trigger" display="lg" :default-open="!mySpaceMode" />
      <button
        v-if="mySpaceMode"
        type="button"
        class="navbar-toggler sidebar-trigger my-space-sidebar-trigger"
        :aria-expanded="String(mySpaceSidebarOpen)"
        aria-label="Abrir menú principal"
        v-b-tooltip.hover.bottom
        title="Menú principal"
        @click.stop="toggleMySpaceSidebar"
      >
        <span class="navbar-toggler-icon"></span>
      </button>
      <b-link class="navbar-brand"
              :class="{
                'navbar-brand--compact': mobileHeaderUsesCompactLogo,
              }"
              :to="mySpaceMode ? { name: 'MySpaceStaff' } : '#'">
        <BrandLogo variant="full" context="navbar" :alt="proyectName" class="d-none d-lg-inline-flex" />
        <BrandLogo v-if="mobileHeaderUsesCompactLogo" variant="compact" context="navbar" :alt="proyectName" class="d-lg-none" />
        <BrandLogo v-else variant="wordmark" context="navbar" :alt="proyectName" class="d-lg-none" />
      </b-link>

      <b-link v-if="mySpaceMode && name !== 'MySpaceStaff'"
              :to="{ name: 'MySpaceStaff' }"
              class="my-space-trigger d-none d-lg-flex">
        <span class="my-space-trigger-icon"><AppIcon icon="auto_awesome" font-scale="0.85" weight="600"></AppIcon></span>
        <span class="my-space-trigger-text">Mi espacio</span>
      </b-link>
      <b-navbar-nav class="ml-auto">
        <b-link v-if="mySpaceMode && name !== 'MySpaceStaff'"
                :to="{ name: 'MySpaceStaff' }"
                class="my-space-trigger-mobile d-lg-none"
                v-b-tooltip.hover.bottom
                title="Ir a Mi espacio">
          <AppIcon icon="auto_awesome" font-scale="1.05" weight="600"></AppIcon>
        </b-link>
        <div class="palette-trigger d-none d-lg-flex" v-if="seePalette && name !== 'MySpaceStaff'" @click="openPalette">
          <AppIcon icon="search" font-scale="0.95"></AppIcon>
          <span class="palette-trigger-text">Buscar</span>
          <span class="palette-trigger-kbd"><kbd>Ctrl</kbd><kbd>K</kbd></span>
        </div>
        <div class="palette-trigger-mobile d-lg-none"
             v-if="seePalette && name !== 'MySpaceStaff'"
             v-b-tooltip.hover.left
             title="Buscar (Ctrl+K)"
          @click="openPalette">
          <AppIcon icon="search" font-scale="1.25" weight="450"></AppIcon>
        </div>
        <LocationStatusIndicator/>
        <RemindersQuickAdd/>
        <MessagesWidget/>
        <NotificationsBell/>
        <b-button
          v-b-toggle.sidebar-aside
          v-if="seeAsside"
          variant="link"
          class="d-flex aside-trigger"
          :class="{ 'aside-trigger--active': asideOpen }"
          aria-label="Más herramientas"
          v-b-tooltip.hover.bottom
          title="Más herramientas"
        >
          <AppIcon
            icon="grid-fill"
            class="aside-trigger-tools"
            font-scale="1.25"
            weight="500"
          ></AppIcon>
        </b-button>
        <DefaultHeaderDropdownAccnt
          :loginComo="loginComo"
          :can-change-profile="arr.seccionOptions.length > 0"
          @change-profile="changeProfile"
        />
      </b-navbar-nav>
    </AppHeader>

    <CommandPalette v-if="seePalette"/>

    <div class="app-body">
      <AppSidebar v-if="!mySpaceMode || mySpaceSidebarOpen" fixed class="my-space-sidebar">
        <SidebarHeader/>
        <SidebarForm/>
        <AppSidebarNav :nav-items="nav" />
        <SidebarFooter/>
        <SidebarMinimizer/>
      </AppSidebar>
      <main class="main">        
        <b-alert show variant="warning" class="alert-clear-cache" v-if="alert.cache.show">
          Ante cualquier problema, limpie la memoria cache. 
          <a href="javascript:void(0);" class="alert-link" @click="clearCache()">Limpiar Cache</a>
          <a href="javascript:void(0);" class="alert-link pull-right hide-clear-cache" title="Ocultar aviso" @click="hideClearCache()">
            <AppIcon icon="toggle-on"></AppIcon>
          </a>
        </b-alert>        

        <div class="container-fluid">
          <!--<router-view :key="$route.path"></router-view>-->
          <tabs-layout />
        </div>
      </main>
      <AppAside fixed>
        <!--aside-->
        <DefaultAside
          v-if="seeAsside"
          :nav-items="nav"
          :shortcuts-only="asideShortcutsOnly"
          @shown="asideOpen = true"
          @hidden="asideOpen = false"
        />
      </AppAside>
    </div>

    <DefaultFooter
      :developer="developer"
      :project-name="proyectName"
      :is-staff="isStaff"
    />

    <b-modal v-model="modal.changePerfil.active"
             headerBgVariant="dark"
             bodyBgVariant="light"
             title="Cambiar Perfil"
             no-close-on-esc
             no-close-on-backdrop
             hide-header-close
             centered>

      <b-row>
        <b-col>
          <b-list-group>
            <b-list-group-item v-for="(item, index) in arr.seccionOptions"
                               :key="index"
                               @click="reloadProfile(item)"
                               href="#"
                               class="flex-column align-items-start container-list-group-item-custom"
                               v-b-tooltip.hover title="Click para cambiar la sessión del usuario">

              <div class="d-flex w-100 justify-content-between">
                <h5 class="mb-1">{{item.selectorSeccionName}}</h5>
                <b-badge variant="secondary" class="badge-selector-profile-custom" pill>{{ item.profileName }}</b-badge>
              </div>

            </b-list-group-item>
          </b-list-group>
        </b-col>
      </b-row>

      <template v-slot:modal-footer>
        <b-button type="button" variant="outline-secondary" @click="cancelProfile()">Cancelar</b-button>
      </template>

    </b-modal>
  </div>
</template>

<script>
import Session from '@/handler/session'
import Profiles from '@/config/profiles'
import Param from '@/config/parameters'
import Helper from '@/handler/helper'
import MenuSuperadmin from '@/components/containers/menuSuperadmin'
import MenuAdmin from '@/components/containers/menuAdmin'
import Menu from '@/components/containers/menu'
import { Header as AppHeader, SidebarToggler, Sidebar as AppSidebar, SidebarFooter, SidebarForm, SidebarHeader, SidebarMinimizer, Aside as AppAside, AsideToggler, Breadcrumb } from '@coreui/vue'
import DefaultHeaderDropdownAccnt from './DefaultHeaderDropdownAccnt'
import NotificationsBell from './NotificationsBell'
import MessagesWidget from './MessagesWidget'
import RemindersQuickAdd from './RemindersQuickAdd'
import LocationStatusIndicator from './LocationStatusIndicator'
import DefaultAside from './DefaultAside'
import DefaultFooter from './DefaultFooter'
import {EventBus} from '@/handler/event-bus'
import serviceAPI from './services'
import Error from '@/handler/error'
import Storage from '@/handler/storageLocal'
import TabsLayout from '@/components/containers/DefaultTabsLayout.vue'
import BrandLogo from '@/components/branding/BrandLogo'
import Theme from '@/config/theme'
import AppSidebarNav from './sidebar/AppSidebarNav'

const CommandPalette = () => import('./command-palette/CommandPalette')

export default {
  name: 'DefaultContainer',
  components: {
    AsideToggler,
    AppHeader,
    AppSidebar,
    AppAside,
    DefaultAside,
    DefaultFooter,
    Breadcrumb,
    DefaultHeaderDropdownAccnt,
    NotificationsBell,
    MessagesWidget,
    RemindersQuickAdd,
    LocationStatusIndicator,
    CommandPalette,
    SidebarForm,
    SidebarFooter,
    SidebarToggler,
    SidebarHeader,
    AppSidebarNav,
    SidebarMinimizer,
    TabsLayout,
    BrandLogo,
  },
  data () {
    return {
      proyectName: '',
      primaryColor: Theme.PRIMARY_COLOR,
      nav: [],
      seeAsside: false,
      asideOpen: false,
      modal: {
        changePerfil: {
          active: false,
        }
      },
      arr: {
        seccionOptions : []
      },
      loginComo: {
        profile: '',
        subProfile: [],
        name: '',
      },
      developer: {
        name: '',
        url: ''
      },
      alert:{
        cache: {
          show: false
        }
      },
      mySpaceModeVersion: 0,
      mySpaceSidebarOpen: false,
    }
  },
  computed: {
    name () {
      return this.$route.name
    },
    list () {
      return this.$route.matched.filter((route) => route.name || route.meta.label )
    },
    isStaff() {
      if (Session.getSession().auth) {
        return parseInt(Session.getSession().auth.user.profile_id) === Profiles.PERSONAL
      }
      return false
    },
    mySpaceMode() {
      this.mySpaceModeVersion
      if (!Session.getSession().auth) return false

      var isPersonal = parseInt(Session.getSession().auth.user.profile_id) === Profiles.PERSONAL &&
        !Session.getSession().auth.user.rol_admin
      if (!isPersonal) return false

      // Aunque el usuario prefiera la interfaz clásica, Mi espacio es una
      // vista inmersiva: al abrirla manualmente no debe arrastrar el aside.
      return this.$route.name === 'MySpaceStaff' || Helper.hasParametersAccess(Param.P81)
    },
    seePalette() {
      this.mySpaceModeVersion
      if (!Session.getSession().auth) return false
      return parseInt(Session.getSession().auth.user.profile_id) === Profiles.PERSONAL &&
             !Session.getSession().auth.user.rol_admin &&
             Helper.hasParametersAccess(Param.P81)
    },
    asideShortcutsOnly() {
      if (!Session.getSession().auth) return false

      const profileId = parseInt(Session.getSession().auth.user.profile_id)

      return profileId === Profiles.CLIENTE ||
             profileId === Profiles.PROVEEDOR ||
             !!Helper.getSeller()
    },
    mobileHeaderUsesCompactLogo() {
      const session = Session.getSession()
      const user = session && session.auth && session.auth.user
      const menu = user && user.permissions && user.permissions.menu

      if (!user || !menu || !this.seeAsside || parseInt(user.profile_id) !== Profiles.PERSONAL) return false

      const enabledRoutes = Object.keys(menu).map(key => menu[key].router)
      const hasAllHeaderWidgets = ['reminders', 'messages', 'notifications']
        .every(router => enabledRoutes.indexOf(router) !== -1)
      const hasMySpaceMobileActions = this.mySpaceMode &&
        this.name !== 'MySpaceStaff' &&
        this.seePalette

      return hasAllHeaderWidgets && hasMySpaceMobileActions
    },
  },
  created () {
    EventBus.$on('Settings:settings', this.setup)
    EventBus.$on('Parameters:parameters', this.refreshMySpaceMode)
  },
  mounted() {
    this.setup()
    this.ensureViewMetadata()
    this.statusClearCache()
    document.addEventListener('click', this.closeMySpaceSidebarOnOutsideClick, true)
  },
  beforeDestroy() {
    EventBus.$off('Settings:settings', this.setup)
    EventBus.$off('Parameters:parameters', this.refreshMySpaceMode)
    document.removeEventListener('click', this.closeMySpaceSidebarOnOutsideClick, true)
    this.closeMySpaceSidebar()
    document.body.classList.remove('my-space-focus-mode')
  },
  watch: {
    mySpaceMode: {
      immediate: true,
      handler(active) {
        document.body.classList.toggle('my-space-focus-mode', active)
        if (!active) this.closeMySpaceSidebar()
      },
    },
  },
  methods: {
    refreshMySpaceMode() {
      this.mySpaceModeVersion++
    },
    ensureViewMetadata() {
      if (!Session.needsViewMetadataRefresh()) return

      serviceAPI.getSessionUser()
      .then(response => {
        Session.setSessionAuth(response.data)
        this.setup()
      })
      .catch(() => {
        // La renovación es preventiva para sesiones anteriores al nuevo
        // contrato de metadata. Los errores normales de sesión/token siguen
        // siendo gestionados por el flujo global de autenticación.
      })
    },
    openPalette() {
      EventBus.$emit('CommandPalette:open')
    },
    toggleMySpaceSidebar() {
      if (this.mySpaceSidebarOpen) {
        this.closeMySpaceSidebar()
        return
      }

      this.mySpaceSidebarOpen = true
      this.$nextTick(() => {
        // Reutiliza las clases de CoreUI para que el sidebar conserve el
        // comportamiento tradicional, pero la vista Mi espacio no se mueve.
        document.body.classList.add('sidebar-show')
      })
    },
    closeMySpaceSidebar() {
      if (!this.mySpaceSidebarOpen) return

      this.mySpaceSidebarOpen = false
      document.body.classList.remove('sidebar-show')
    },
    closeMySpaceSidebarOnOutsideClick(event) {
      if (!this.mySpaceSidebarOpen) return

      const target = event.target
      if (target.closest('.my-space-sidebar') || target.closest('.my-space-sidebar-trigger')) return

      this.closeMySpaceSidebar()
    },
    setup () {      
      if(Session.getSession().settings) {
        this.proyectName = Session.getSession().settings.proyect_name
        this.createCSS()
        this.configMenu()
        this.configLoginComo()
        this.configSelectProfile()
        this.configDeveloper()
      }
    },
    createCSS() {
      var hoja = document.createElement('style')
      hoja.innerHTML = ""
      hoja.innerHTML = hoja.innerHTML  + ".b-pagination .page-item.active .page-link { background-color: " + this.primaryColor + "; border-color: " + this.primaryColor + "; }"
      hoja.innerHTML = hoja.innerHTML  + "a {color: " + this.primaryColor + ";}"
      hoja.innerHTML = hoja.innerHTML  + "a:hover {color: " + this.primaryColor + "; filter: brightness(85%);}"
      hoja.innerHTML = hoja.innerHTML  + ".selected-custom {color: " + this.primaryColor + ";}"
      hoja.innerHTML = hoja.innerHTML  + ".selected-custom:hover {color: " + this.primaryColor + "; filter: brightness(85%);}"
      hoja.innerHTML = hoja.innerHTML  + ".btn-link {color: " + this.primaryColor + ";}"
      hoja.innerHTML = hoja.innerHTML  + ".btn-link:hover {color: " + this.primaryColor + "; filter: brightness(85%);}"
      hoja.innerHTML = hoja.innerHTML  + ".list-group-item-action:hover, .list-group-item-action:focus, .list-group-item-action.active {background-color: " + this.primaryColor + " !important; color: var(--app-color-on-primary) !important; border-color: " + this.primaryColor + " !important;}"
      hoja.innerHTML = hoja.innerHTML  + ".list-group-item-action:hover .text-muted, .list-group-item-action:focus .text-muted, .list-group-item-action.active .text-muted {color: rgba(255, 255, 255, 0.8) !important;}"
      hoja.innerHTML = hoja.innerHTML  + ".aside-menu .nav-tabs .nav-link.active {color: " + this.primaryColor + ";}"
      hoja.innerHTML = hoja.innerHTML  + ".sidebar .nav-link.active {color: var(--app-color-on-primary) !important;}"
      hoja.innerHTML = hoja.innerHTML  + ".nav-pills .nav-link.active, .nav-pills .show > .nav-link {background-color: " + this.primaryColor + " !important; color: var(--app-color-on-primary) !important;}"
      hoja.innerHTML = hoja.innerHTML  + ".b-sidebar .list-group-item:hover, .b-sidebar .list-group-item.active, .b-sidebar .list-group-item:focus {background-color: " + this.primaryColor + " !important; color: var(--app-color-on-primary) !important; border-color: " + this.primaryColor + " !important;}"
      
      document.body.appendChild(hoja);
    },
    configMenu() {
      if(Session.getSession().auth) {
        if(Session.getSession().auth.user.profile_id == Profiles.SUPERADMIN) {
          this.nav = MenuSuperadmin.getItems()
          this.seeAsside = false
        } else {
          if(Session.getSession().auth.user.profile_id == Profiles.PERSONAL &&
             Session.getSession().auth.user.rol_admin) {
            this.nav = MenuAdmin.getItems()
            this.seeAsside = false
          } else {
            this.nav = Menu.getItems(Session.getSession().auth.user.permissions.menu)
            this.seeAsside = true
          }
        }
      }
    },
    configLoginComo() {
      if(Session.getSession().auth) {
        var activeUser = Session.getSession().auth.user
        var activeProfileId = parseInt(activeUser.profile_id)

        this.loginComo.profile = ''
        this.loginComo.name = ''
        this.loginComo.subProfile = []

        if(activeProfileId === Profiles.SUPERADMIN) {
          this.loginComo.profile = 'Superadmin'
        }
        if(activeProfileId === Profiles.PERSONAL) {
          this.loginComo.profile = 'Staff'

          if(Helper.getEmployee() && Helper.getEmployee().category) {
            this.loginComo.subProfile.push(Helper.getEmployee().category.name)
          }
          if(Helper.getSeller() && this.loginComo.subProfile.indexOf('Comercial') === -1) {
            this.loginComo.subProfile.push('Comercial')
          }

          if(!this.loginComo.subProfile.length) {
            var sessionRoles = activeUser.roles || activeUser.Roles || []
            var roles = Array.isArray(sessionRoles) ? sessionRoles : []

            roles.forEach(role => {
              if(parseInt(role.profile_id) === Profiles.PERSONAL &&
                 role.name &&
                 this.loginComo.subProfile.indexOf(role.name) === -1) {
                this.loginComo.subProfile.push(role.name)
              }
            })
          }
        }
        if(activeProfileId === Profiles.CLIENTE) {
          this.loginComo.profile = 'Cliente'
        }
        if(activeProfileId === Profiles.PROVEEDOR) {
          this.loginComo.profile = 'Proveedor'
        }

        if (!activeUser.selector_session){
          if(activeProfileId !== Profiles.SUPERADMIN) {
            if(activeUser.rol_admin === false) {
              this.modal.changePerfil.active = true
            }
          }
        } else {
          this.loginComo.name = activeUser.selector_session_name
        }
      }
    },
    configSelectProfile() {
      var arrayProfiles = []
      if(Session.getSession().auth) {
        var permission = Session.getSession().auth.user.profile_access
        for (const prop in permission.staff) {
          arrayProfiles.push({
            profileId: Profiles.PERSONAL,
            profileName: 'Staff',
            selectorSeccion: permission.staff[prop].id,
            selectorSeccionName: permission.staff[prop].name
          })
        }
        for (const prop in permission.customers) {
          arrayProfiles.push({
            profileId: Profiles.CLIENTE,
            profileName: 'Cliente',
            selectorSeccion: permission.customers[prop].id,
            selectorSeccionName: permission.customers[prop].name
          })
        }
        for (const prop in permission.suppliers) {
          arrayProfiles.push({
            profileId: Profiles.PROVEEDOR,
            profileName: 'Proveedor',
            selectorSeccion: permission.suppliers[prop].id,
            selectorSeccionName: permission.suppliers[prop].name
          })
        }
        this.arr.seccionOptions = arrayProfiles
      }
    },
    changeProfile() {
      var hoja = document.createElement('style')
      hoja.innerHTML = ""
      hoja.innerHTML = hoja.innerHTML  + ".container-list-group-item-custom:hover { background: " + this.primaryColor + "; color: white;}"
      document.body.appendChild(hoja);
      this.modal.changePerfil.active = true
    },
    reloadProfile(item) {
      let loader = this.$loading.show();
      var result = serviceAPI.configSession(item);

      result.then(() => {

        var result_1 = serviceAPI.getSessionUser();
        result_1.then((response_1) => {
          var data = response_1.data

          Session.setSessionAuth(data)
          this.$router.push({ name: 'Init' })
          this.modal.changePerfil.active = false

          loader.hide()
        })
        .catch(error_1 => {
          loader.hide()
          this.$awn.alert(Error.showError(error_1))
        })

      })
      .catch(error => {
        loader.hide()
        this.$awn.alert(Error.showError(error));
      })
    },
    cancelProfile(){
      if (!Session.getSession().auth.user.selector_session){
        this.modal.changePerfil.active = true
        this.$awn.alert("No se cargo el perfil por defecto")
      } else {
        this.modal.changePerfil.active = false
      }
    },
    configDeveloper() {
      if(Session.getSession().auth) {
        if(Session.getSession().auth.developer) {
          this.developer.name = Session.getSession().auth.developer.name
          this.developer.url = Session.getSession().auth.developer.url
        }
      }
    },

    // CACHE
    clearCache() {
      this.$router.push({ name: 'Clear'}).catch(err => {
          
          // Ignore the vuex err regarding  navigating to the page they are already on.
          if ( err.name !== 'NavigationDuplicated' &&
              !err.message.includes('Avoided redundant navigation to current location')) {
          
          this.$bvToast.toast(Error.showError(err), {
            title: 'Error',
            variant: 'danger',
            solid: true,
          })
          }
      });           
    },        
    statusClearCache() {              
      this.alert.cache.show = false
      Storage.setValue('show_alert_clear_cache', false)
    },      
    hideClearCache() {
      this.alert.cache.show = false
      Storage.setValue('show_alert_clear_cache', false)
    }
  }
}
</script>

<style>
/* CoreUI conserva las clases sidebar-*-show en <body> al desmontar el
   sidebar. En Mi espacio anulamos ese margen residual: la vista es inmersiva
   y debe ocupar el ancho completo sin requerir recargar la página. */
body.my-space-focus-mode .main,
body.my-space-focus-mode .app-footer {
  margin-left: 0 !important;
}

  body {
    background-color: var(--app-color-canvas) !important;
  }
  .main .container-fluid {
    padding: 0 15px;
    /*margin-top: 15px;*/
  }
  .app-header .navbar-toggler.sidebar-trigger {
    min-width: 40px;
    width: 40px;
    height: 40px;
    margin: 0 5px;
    padding: 0;
    border: 1px solid var(--app-color-border) !important;
    border-radius: 10px;
    background: var(--app-color-surface-subtle);
    box-shadow: none !important;
    color: var(--app-color-text-secondary);
    transition: background-color 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
  }
  .app-header .navbar-toggler.sidebar-trigger:hover,
  .app-header .navbar-toggler.sidebar-trigger:focus,
  .app-header .navbar-toggler.sidebar-trigger:active {
    border-color: var(--app-color-primary-200) !important;
    outline: 0 !important;
    background: var(--app-color-primary-soft);
    color: var(--app-color-primary);
    box-shadow: none !important;
    transform: translateX(1px);
  }
  .app-header .navbar-toggler.sidebar-trigger .navbar-toggler-icon {
    width: 20px;
    height: 20px;
  }
  .aside-trigger.btn {
    position: relative;
    align-self: center;
    align-items: center;
    justify-content: center;
    min-width: 40px;
    width: 40px;
    height: 40px;
    margin: 0 5px;
    padding: 0;
    border: 1px solid var(--app-color-border);
    border-radius: 10px;
    background: var(--app-color-surface-subtle);
    box-shadow: none;
    color: var(--app-color-text-secondary);
    transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease, transform 0.15s ease;
  }
  .aside-trigger.btn:hover,
  .aside-trigger.btn:focus {
    border-color: var(--app-color-primary-200);
    background: var(--app-color-primary-soft);
    box-shadow: none;
    color: var(--app-color-primary);
    text-decoration: none;
    transform: translateX(-1px);
  }
  .aside-trigger.btn.aside-trigger--active {
    border-color: var(--app-color-primary-300);
    background: var(--app-color-primary-soft-hover);
    color: var(--app-color-primary-active);
  }
  .aside-trigger-tools {
    font-size: 1rem;
    transition: transform 0.15s ease;
  }
  .aside-trigger.btn:hover .aside-trigger-tools,
  .aside-trigger.btn:focus .aside-trigger-tools {
    transform: scale(1.08);
  }
  @media (max-width: 991.98px) {
    .app-header .navbar-toggler.sidebar-trigger {
      min-width: 36px;
      width: 36px;
      height: 36px;
      border-radius: 9px;
    }
    .aside-trigger.btn {
      min-width: 36px;
      width: 36px;
      height: 36px;
      margin: 0 2px;
      padding: 0;
      border-radius: 9px;
    }
    .aside-trigger-tools {
      font-size: 0.95rem;
    }
  }
  .card {
    margin-bottom: 10px;
  }
  .table th, .table td {
    padding: 5px;
  }
  .vld-overlay.is-full-page {
    z-index: 99999 !important;
    background-color: var(--app-color-overlay) !important;
  }
  .btn-sm, .btn-group-sm > .btn {
    padding: 0.15rem 0.5rem;
  }
  .modal-header {
    font-weight: 500;
    font-size: 17px;
  }
  .material-icons {
    font-size: 18px;
  }
  .badge-selector-profile-custom {
    padding-top: 0.6em;
  }
  legend {
    font-weight: 600;
  }
  @media (min-width: 992px){
    .sidebar-minimized .sidebar section :not(.nav-dropdown-items) > .nav-item:last-child::after {
      display: none;
    }
  }
  .module-help-information {
    cursor: pointer;
  }
  
  .widget-custom .card-header{
    padding: 0px;
  }
  .widget-custom .card-body{
    padding: 5px;
  }
  .widget-header-custom {
    padding-top: 8px;
    padding-bottom: 8px;
    padding-left: 15px;
    padding-right: 15px;
  }
  .widget-info-custom {
    border: 1px solid var(--app-color-primary-100);
    background: var(--app-color-primary-soft);
    color: var(--app-color-primary);
    border-radius: 50%;
    cursor: pointer;
  }
  .widget-ultima-actualizacion {
    cursor: pointer;
  }
  .widget-ultima-actualizacion span {
    background: var(--app-color-surface-muted);
    padding-left: 5px;
    padding-right: 5px;
  }
  .card-footer {
    padding-top: 5px;
    padding-bottom: 5px;
  }
  .widget-router-custom {
    position: absolute;
    left: 1px;
    padding-left: 5px;
    padding-right: 5px;
    font-size: 10px;
    bottom: 1px;    
  }
  @media (min-width: 992px) {
    .app-header .navbar-brand,
    .brand-minimized .app-header .navbar-brand {
      width: 155px;
      flex: 0 0 155px;
    }
  }
  @media (max-width: 991.98px) {
    .app-header .navbar-brand {
      left: 46px;
      width: 70px;
      margin-left: 0;
    }
    /* Sin hamburguesa (mySpaceMode/parámetro 81 activo) el logo pasa a ocupar
       ese lugar, pegado al margen izquierdo — el selector compuesto sube la
       especificidad para ganarle a la regla de arriba sin depender del orden
       en el archivo (misma media query, mismo elemento). width:auto +
       justify-content:flex-start sacan el padding de centrado de la caja de
       70px (pensada para cuando había hamburguesa antes del logo). El left
       queda casi en 0 porque BrandLogo (variant="wordmark") ya trae su
       propio recuadro con aire interno propio, y .app-header no tiene
       padding — cualquier left agregado acá se suma directo a ese aire y se
       ve como margen de más. */
    .app-header .navbar-brand.navbar-brand--flush {
      left: 4px;
      width: auto;
      justify-content: flex-start;
    }
    .app-header .navbar-brand.navbar-brand--compact {
      width: 48px;
    }
  }
  .palette-trigger {
    display: flex;
    align-items: center;
    gap: 8px;
    align-self: center;
    padding: 6px 12px;
    margin: 0 6px;
    border: 1px solid var(--app-color-border);
    border-radius: 18px;
    background: var(--app-color-surface-subtle);
    color: var(--app-color-text-muted);
    font-size: 12px;
    cursor: pointer;
    transition: border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease;
  }
  .palette-trigger:hover {
    border-color: var(--app-color-primary);
    background: var(--app-color-primary-soft);
    color: var(--app-color-primary);
  }
  .palette-trigger-text {
    min-width: 80px;
  }
  .palette-trigger-kbd {
    display: flex;
    gap: 2px;
  }
  .palette-trigger-kbd kbd {
    padding: 1px 5px;
    border: 1px solid var(--app-color-border);
    border-radius: 4px;
    background: var(--app-color-surface);
    color: var(--app-color-text-muted);
    font-size: 10px;
  }
  .palette-trigger-mobile {
    position: relative;
    width: 34px;
    height: 34px;
    margin: 0 6px;
    align-self: center;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: var(--app-color-icon);
    cursor: pointer;
    transition: background-color 0.15s ease;
  }
  .palette-trigger-mobile:hover {
    background-color: var(--app-color-primary-soft);
    color: var(--app-color-primary);
  }
  /* Ancla a "Mi espacio" (desktop): botón propio, no un link plano — más
     grande y sin negrita para que lea como texto normal, con un pequeño
     levantamiento + sombra al hover que le da sensación de botón. No vive en
     el menú a propósito porque casi no se va a abrir. */
  .my-space-trigger {
    display: flex;
    align-items: center;
    gap: 8px;
    align-self: center;
    padding: 8px 16px;
    margin: 0 0 0 10px;
    border: 1px solid var(--app-color-border);
    border-radius: 10px;
    background: var(--app-color-surface-subtle);
    color: var(--app-color-primary);
    font-size: 14px;
    font-weight: 400;
    cursor: pointer;
    transition: border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
  }
  .my-space-trigger-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 17px;
    color: inherit;
  }
  .my-space-trigger:hover,
  .my-space-trigger:focus {
    border-color: var(--app-color-primary-200);
    background: var(--app-color-primary-soft);
    color: var(--app-color-primary-active);
    text-decoration: none;
    transform: translateY(-2px);
    box-shadow: var(--app-shadow-sm);
  }
  .my-space-trigger-text {
    white-space: nowrap;
  }
  .my-space-trigger-mobile {
    position: relative;
    width: 36px;
    height: 36px;
    margin: 0 0 0 4px;
    align-self: center;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--app-color-border);
    border-radius: 9px;
    background: var(--app-color-surface-subtle);
    color: var(--app-color-primary);
    cursor: pointer;
    transition: border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
  }
  .my-space-trigger-mobile:hover,
  .my-space-trigger-mobile:focus {
    border-color: var(--app-color-primary-200);
    background: var(--app-color-primary-soft);
    color: var(--app-color-primary-active);
    transform: translateY(-2px);
    box-shadow: var(--app-shadow-sm);
    text-decoration: none;
  }
</style>
<style scoped>
  .alert-clear-cache {
    padding: 0px;
    padding-left: 1.25rem;
    font-size: 10px;
    margin-bottom: 0px;    
  }
  .alert-clear-cache .hide-clear-cache {      
    padding-right: 1.25rem;
    font-size: 15px;
    line-height: 0;      
  }
</style>
